import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { schoolService, userService } from '../../../services';

// URL state parsers
const searchParser = parseAsString.withDefault('');
const classificationParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const unitParser = parseAsString.withDefault('');
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

const useTeacher = (schoolId?: string) => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [search, setSearch] = useQueryState('search', searchParser);
  const [classification, setClassification] = useQueryState('classification', classificationParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [unit, setUnit] = useQueryState('unit', unitParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters = useMemo(() => ({
    search: search || undefined,
    classification: classification || undefined,
    status: status || undefined,
    unit: unit || undefined,
  }), [search, classification, status, unit]);

  // Query key cho caching
  const queryKey = ['school-teachers', schoolId];

  // Fetch teachers với react-query using new API
  const {
    data: teachersResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => schoolService.getSchoolTeachers(schoolId!),
    enabled: !!schoolId, // Only run query if schoolId is provided
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Filter teachers based on local filters
  const filteredTeachers = useMemo(() => {
    if (!teachersResponse?.data) return [];
    
    return teachersResponse.data.filter((teacher: any) => {
      const matchesSearch = !filters.search || 
        (teacher.user?.name ?? teacher.name)?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (teacher.user?.email ?? teacher.email)?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (teacher.schoolUserData?.metadata?.unit?.toLowerCase() || '').includes(filters.search.toLowerCase()) ||
        (teacher.schoolUserData?.metadata?.subjects?.some((subject: string) => 
          subject.toLowerCase().includes(filters.search!.toLowerCase())) || false);
      
      const matchesClassification = !filters.classification || 
        teacher.schoolUserData?.metadata?.classification === filters.classification;
      
      const matchesStatus = !filters.status || 
        teacher.schoolUserData?.status === filters.status;
      
      const matchesUnit = !filters.unit || 
        teacher.schoolUserData?.metadata?.unit === filters.unit;
      
      return matchesSearch && matchesClassification && matchesStatus && matchesUnit;
    });
  }, [teachersResponse?.data, filters]);

  // BE gắn giáo viên vào trường qua schoolId (ghi school_users); thông tin giảng dạy lưu ở metadata
  const createTeacherMutation = useMutation({
    // Tạo tài khoản không gửi lại an toàn được (dễ tạo trùng), còn retry mặc định của app treo khi tab mất focus
    retry: false,
    mutationFn: (data: any) =>
      userService.createUser({
        name: data.name,
        email: data.email,
        phone: data.phone,
        user_name: data.user_name,
        role: 'teacher',
        type: 'teacher',
        schoolId: data.schoolId,
        metadata: {
          unit: data.unit,
          subjects: String(data.subjects || '').split(',').map((s: string) => s.trim()).filter(Boolean),
          classification: data.classification,
          monthlyQuota: Number(data.monthlyQuota) || 0,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-teachers'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.classification !== undefined) setClassification(newFilters.classification || null);
    if (newFilters.status !== undefined) setStatus(newFilters.status || null);
    if (newFilters.unit !== undefined) setUnit(newFilters.unit || null);
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [setSearch, setClassification, setStatus, setUnit, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSearch(null);
    setClassification(null);
    setStatus(null);
    setUnit(null);
    setPage(1);
  }, [setSearch, setClassification, setStatus, setUnit, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof typeof filters) => {
    switch (filterKey) {
      case 'search':
        setSearch(null);
        break;
      case 'classification':
        setClassification(null);
        break;
      case 'status':
        setStatus(null);
        break;
      case 'unit':
        setUnit(null);
        break;
    }
    setPage(1);
  }, [setSearch, setClassification, setStatus, setUnit, setPage]);

  return {
    // Data
    teachers: filteredTeachers,
    pagination: {
      total: filteredTeachers.length,
      current_page: page,
      per_page,
      total_pages: Math.ceil(filteredTeachers.length / per_page)
    },
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createTeacher: createTeacherMutation.mutate,

    // Mutation states
    isCreating: createTeacherMutation.isPending,

    // Mutation objects for advanced handling
    createTeacherMutation,
  };
};

export default useTeacher;
